import Link from 'next/link';
import {
  Children,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
} from 'react';
import type {
  ItemBaseProps,
  ChildlessItemProps,
  TNavigationCollection,
} from './navigation-collection.types';
import {
  StyledCollection,
  StyledSubCollection,
  StyledLi,
  StyledNavigationLabel,
  StyledItemBadgeSale,
  StyledItemBadgeInclusive,
  StyledItemImage,
  StyledItemMain,
  StyledItemMainChild,
  StyledItemMeta,
  StyledItemBadgeSaleRose,
} from './navigation-collection.styles';
import { useScreenNameAtLeast } from '../../../hooks/use-screen-name-at-least';
import { EventClickElement } from '@tuic/tracking';
import { twJoin } from 'tailwind-merge';
import { LegacyIcon, type LegacyIconName } from '../LegacyIcon';
import { Icon, type IconName } from '../../../core-components';

/**
 * Renders the contents of a single navigation item.
 */
const renderSingleItem = (
  Wrapper: ElementType,
  props: ItemBaseProps | ChildlessItemProps,
  hasChevron: boolean = true,
  hasChildren?: boolean,
  isActive?: boolean,
  isDesktop?: boolean,
) => {
  // Not all props are available in the props object, so we need to destructure them while setting defaults.
  let {
    children,
    itemTitle,
    href,
    itemIcon,
    isLegacyIcon = true,
    ...attrs
  } = {
    // Add all actual props
    ...props,

    // Guard against missing props and default them to undefined.
    itemTitle: 'itemTitle' in props ? props.itemTitle : undefined,
    href: 'href' in props ? props.href : undefined,
    itemIcon: 'itemIcon' in props ? props.itemIcon : undefined,
    isLegacyIcon: 'isLegacyIcon' in props ? props.isLegacyIcon : true,
  };

  // Some items, like the `Image` item, provide their own children, so we need to check for that.
  // If no children are provided, we create the default children.
  if (!children) {
    children = (
      <>
        <StyledNavigationLabel>
          {/* Icons are optional, so we need to check for them. */}
          {itemIcon && isLegacyIcon && (
            <div>
              <LegacyIcon
                name={itemIcon as LegacyIconName}
                size={isDesktop ? 48 : 32}
              />
            </div>
          )}
          {itemIcon && !isLegacyIcon && (
            <div>
              <Icon
                name={itemIcon as IconName}
                className="size-[32px] lg:size-[48px]"
              />
            </div>
          )}

          <div>{itemTitle}</div>
        </StyledNavigationLabel>

        {/* Not all items want to have a chevron, so we need to check for that. */}
        {hasChevron && (
          <LegacyIcon
            name="chevron-right"
            size={32}
            className={twJoin(
              'lg:hidden',
              hasChildren && 'rotate-90',
              hasChildren && isActive && '-rotate-90',
            )}
          />
        )}
      </>
    );
  }

  // If no href is provided, we render a button instead of an anchor.
  if (!href) {
    return (
      <Wrapper
        asChild
        isActive={isActive}
        data-track-click-element={EventClickElement.Button}
        data-track-click-label={itemTitle}
      >
        <button {...(attrs as ButtonHTMLAttributes<HTMLButtonElement>)}>
          {children}
        </button>
      </Wrapper>
    );
  }

  // If an href is provided, we render an anchor.
  return (
    <Wrapper
      asChild
      isActive={isActive}
      data-track-click-element={EventClickElement.Button}
      data-track-click-label={itemTitle}
    >
      <Link href={href} {...(attrs as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </Link>
    </Wrapper>
  );
};

/**
 * Renders an `Inclusive Badge` item.
 */
const ItemBadgeInclusive: TNavigationCollection['BadgeInclusive'] = ({
  mobile,
  desktop,
  ...props
}) => {
  return (
    <StyledLi mobile={mobile ?? true} desktop={desktop ?? false}>
      {renderSingleItem(StyledItemBadgeInclusive, props)}
    </StyledLi>
  );
};

/**
 * Renders a `Sale Badge` item.
 */
const ItemBadgeSale: TNavigationCollection['BadgeSale'] = ({
  mobile,
  desktop,
  ...props
}) => {
  return (
    <StyledLi mobile={mobile ?? true} desktop={desktop ?? false}>
      {renderSingleItem(StyledItemBadgeSale, props)}
    </StyledLi>
  );
};

/**
 * Renders a `Sale Rose Badge` item.
 */
const ItemBadgeSaleRose: TNavigationCollection['BadgeSaleRose'] = ({
  mobile,
  desktop,
  ...props
}) => {
  return (
    <StyledLi mobile={mobile ?? true} desktop={desktop ?? false}>
      {renderSingleItem(StyledItemBadgeSaleRose, props)}
    </StyledLi>
  );
};

/**
 * Renders an `Image` item.
 */
const ItemImage: TNavigationCollection['Image'] = ({
  mobile,
  desktop,
  ...props
}) => {
  return (
    <StyledLi mobile={mobile ?? true} desktop={desktop ?? false}>
      {renderSingleItem(StyledItemImage, props)}
    </StyledLi>
  );
};

/**
 * Renders a `Main` item.
 */
const ItemMain: TNavigationCollection['Main'] = ({
  mobile,
  desktop,
  children,
  ...props
}) => {
  const { isActive, ...otherProps } = {
    ...props,
    isActive: 'isActive' in props ? props.isActive : undefined,
  };

  return (
    <StyledLi
      mobile={mobile ?? true}
      desktop={desktop ?? true}
      data-track-click-category={otherProps.itemTitle || ''}
    >
      {renderSingleItem(
        StyledItemMain,
        otherProps,
        true,
        !!Children && !!Children.count(children),
        isActive,
      )}
      {children && (
        <StyledSubCollection open={!!isActive}>{children}</StyledSubCollection>
      )}
    </StyledLi>
  );
};

/**
 * Renders a child of a `Main` item.
 */
const ItemMainChild: TNavigationCollection['MainChild'] = ({
  mobile,
  desktop,
  children,
  ...props
}) => {
  const isDesktop = useScreenNameAtLeast('lg');

  return (
    <StyledLi mobile={mobile ?? true} desktop={desktop ?? true}>
      {renderSingleItem(
        StyledItemMainChild,
        props,
        false,
        false,
        false,
        isDesktop,
      )}
    </StyledLi>
  );
};

/**
 * Renders a `Meta` item.
 */
const ItemMeta: TNavigationCollection['Meta'] = ({
  mobile,
  desktop,
  ...props
}) => {
  return (
    <StyledLi mobile={mobile ?? true} desktop={desktop ?? false}>
      {renderSingleItem(StyledItemMeta, props)}
    </StyledLi>
  );
};

/**
 * Renders a collection of navigation items.
 * Review {@link TNavigationCollection} for more information about the expected structure.
 * Use the provided child components to build up your navigation tree:
 *
 * - {@link NavigationCollection.BadgeInclusive}
 * - {@link NavigationCollection.BadgeSale}
 * - {@link NavigationCollection.Image}
 * - {@link NavigationCollection.Main}
 *   - {@link NavigationCollection.MainChild}
 * - {@link NavigationCollection.Meta}
 *
 * ```tsx
 * <NavigationCollection>
 *   <NavigationCollection.Main itemTitle="Main Item" href="/main-item" />
 *   <NavigationCollection.Main itemTitle="Main Item with Children" href="/main-item-with-children">
 *     <NavigationCollection.MainChild itemTitle="Child Item" href="/child-item" />
 *   </NavigationCollection.Main>
 *   <NavigationCollection.BadgeInclusive itemTitle="Inclusive Badge" href="/inclusive-badge" />
 * </NavigationCollection>
 * ```
 */
const NavigationCollection: TNavigationCollection = ({ children }) => {
  return <StyledCollection open={true}>{children}</StyledCollection>;
};

NavigationCollection.BadgeInclusive = ItemBadgeInclusive;
NavigationCollection.BadgeSale = ItemBadgeSale;
NavigationCollection.BadgeSaleRose = ItemBadgeSaleRose;
NavigationCollection.Image = ItemImage;
NavigationCollection.Main = ItemMain;
NavigationCollection.MainChild = ItemMainChild;
NavigationCollection.Meta = ItemMeta;

export default NavigationCollection;
